import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { Dropdown } from "primereact/dropdown";

import { Can } from "configs/CASLContext";
import { findUsers } from "services/UserService";
import { UserContext } from "components/user/UserContext";
import { UserEditor } from "components/user/UserEditor";
import { create, edit, User, view, X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import BlockDetail from "components/block-detail/BlockDetail";
import { checkDateMonth, dateFormat1, dateFormat2, formatDate, range2 } from "utils/date";
import { Calendar } from "primereact/calendar";
import { actionUpdate, actionView } from "utils/action";

export const UserList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["user", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const userContext = useContext(UserContext);
    const { user, resetUser, setUser, users, setUsers, setSubmitted, setUserDialog } = userContext;

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(false);

    const now = new Date();

    const beforeNow = new Date(new Date().setDate(new Date().getDate() - range2));

    const [formatDateVal, setFormatDateVal] = useState(dateFormat1);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            idNumber: { value: "", matchMode: "contains" },
            fullName: { value: "", matchMode: "contains" },
            email: { value: "", matchMode: "contains" },
            type: { value: null, matchMode: "equals" },
            enabled: { value: null, matchMode: "equals" },
            phoneNumber: { value: "", matchMode: "contains" },
            fromDate: { value: beforeNow, matchMode: "equals" },
            toDate: { value: now, matchMode: "equals" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    const userTypes = [
        { label: t("citizen"), value: "CITIZEN" },
        { label: t("foreign"), value: "FOREIGN" },
    ];

    const states = [
        { label: t("user.-1"), value: -1 },
        { label: t("user.0"), value: 0 },
        { label: t("user.1"), value: 1 },
        { label: t("user.2"), value: 2 },
        { label: t("user.3"), value: 3 },
        { label: t("user.4"), value: 4 },
        { label: t("user.31"), value: 31 },
        { label: t("user.97"), value: 97 },
        { label: t("user.98"), value: 98 },
        { label: t("user.99"), value: 99 },
    ];

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    const loadLazyData = () => {
        setLoading(true);

        findUsers(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                setUsers(dataRes);
            })
            // .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openNewDialog = () => {
        resetUser();
        setSubmitted(false);
        setUserDialog(true);
    };

    const openEditDialog = (user) => {
        setUser({ ...user, action: actionUpdate });
        setUserDialog(true);
    };

    const openViewDialog = (user) => {
        setUser({ ...user, action: actionView });
        setUserDialog(true);
    };

    const checkboxFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <TriStateCheckbox value={options.value} onChange={(e) => options.filterCallback(e.value)} />
            </div>
        );
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterCallback(e.target?.value || "")} />
            </div>
        );
    };

    const userTypeFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <Dropdown style={{ width: "100%", margin: "auto", height: "auto", padding: "10px" }} value={options.value} options={userTypes} onChange={(e) => options.filterCallback(e.value)} showClear={true} />
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-0">
                    <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                </div>
            </React.Fragment>
        );
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("user.list")}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={t("search", { ns: "common" })} />
            </span>
        </div>
    );

    const statusTemplate = (rowData) => {
        let text = "";
        if (rowData.level == 0 && rowData.state == 0) {
            text = "Tài khoản hoạt động ở mức 0";
        } else if (rowData.level == 1 && rowData.state == 1 && !rowData.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp cần đổi mật khẩu";
        } else if (rowData.level == 1 && rowData.state == 1 && rowData.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp không cần đổi mật khẩu";
        } else if (rowData.level == 1 && rowData.state == 2) {
            text = "Tài khoản hoạt động mức 1";
        } else if (rowData.level == 1 && rowData.state == 97) {
            text = "Tài khoản bị khóa tạm thời";
        } else if (rowData.level == 1 && rowData.state == 98) {
            text = "Tài khoản bị khóa tạm thời";
        } else if (rowData.level == 2 && rowData.state == 1 && !rowData.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp cần đổi mật khẩu";
        } else if (rowData.level == 2 && rowData.state == 1 && rowData.isUpdate) {
            text = "Tài khoản chờ kích hoạt thiết bị - Trường hợp không cần đổi mật khẩu";
        } else if (rowData.level == 2 && rowData.state == 2) {
            text = "Tài khoản hoạt động mức 2";
        } else if (rowData.level == 2 && rowData.state == 97) {
            text = "Tài khoản bị khóa tạm thời";
        } else if (rowData.level == 2 && rowData.state == 98) {
            text = "Tài khoản bị khóa vĩnh viễn";
        }
        return <div className="flex align-items-center justify-content-center actions">{text}</div>;
    };

    const userTypeBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{t(String(rowData.type).toLowerCase())}</p>
                </div>
            </>
        );
    };

    const checkboxBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <Checkbox checked={rowData.enabled} readOnly />
                </div>
            </>
        );
    };

    const LevelBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">Mức {rowData.level}</div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                {/* <Can I={edit} a={User}>
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button mr-2" tooltip={t("edit", { ns: "common" })} onClick={() => openEditDialog(rowData)} />
                </Can> */}
                <Can I={view} a={User}>
                    <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openViewDialog(rowData)} />
                </Can>
            </div>
        );
    };

    const stateBodyTemplate = (rowData) => {
        return <div className="flex align-items-center justify-content-center actions">{states.find((e) => e.value === rowData.stateDetail?.type)?.label}</div>;
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <>
            <div className="grid">
                {/* tên đăng nhập */}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                            htmlFor="idNumber"
                        >
                            {t("user.idNumber")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="idNumber"
                                name="idNumber"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            idNumber: {
                                                ...lazyParams.filters.idNumber,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* số đt */}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                            htmlFor="phoneNumber"
                        >
                            {t("phoneNumber")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                id="phoneNumber"
                                name="phoneNumber"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            phoneNumber: {
                                                ...lazyParams.filters.phoneNumber,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>

                {/* Họ và tên  */}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                            htmlFor="fullName"
                        >
                            {t("user.fullName")}
                        </label>
                        <div className="col">
                            <InputText
                                style={{
                                    width: "100%",
                                }}
                                value={lazyParams?.filters?.fullName.value}
                                id="fullName"
                                name="fullName"
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fullName: {
                                                ...lazyParams.filters.fullName,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText>
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid">
                {/* Ngày nhận từ */}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "100px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                            htmlFor="fromDate"
                        >
                            {t("user.fromDate")}
                        </label>
                        <div className="col">
                            <Calendar
                                style={{ width: "100%" }}
                                value={lazyParams.filters.fromDate.value}
                                onChange={(e) => {
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fromDate: {
                                                ...lazyParams.filters.fromDate,
                                                value: e.value,
                                            },
                                        },
                                    });
                                }}
                                onBlur={() => {
                                    setFormatDateVal(dateFormat1);
                                }}
                                onFocus={() => {
                                    setFormatDateVal(dateFormat2);
                                }}
                                name="fromDate"
                                id="fromDate"
                                maxDate={lazyParams.filters.toDate.value}
                                dateFormat={formatDateVal}
                            ></Calendar>
                            {/* <InputText
                                style={{
                                    width: "100%",
                                }}
                                type="date"
                                id="fromDate"
                                name="fromDate"
                                value={lazyParams.filters.fromDate.value}
                                max={lazyParams.filters.toDate.value}
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            fromDate: {
                                                ...lazyParams.filters.fromDate,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText> */}
                        </div>
                    </div>
                </div>

                {/* đến */}
                <div className="col-12 md:col-4">
                    <div className="grid custom-form">
                        <label
                            className="col-fixed text-center"
                            style={{
                                width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                            htmlFor="toDate"
                        >
                            {t("user.toDate")}
                        </label>
                        <div className="col">
                            <Calendar
                                style={{ width: "100%" }}
                                value={lazyParams.filters.toDate.value}
                                onChange={(e) => {
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            toDate: {
                                                ...lazyParams.filters.toDate,
                                                value: e.value,
                                            },
                                        },
                                    });
                                }}
                                onBlur={() => {
                                    setFormatDateVal(dateFormat1);
                                }}
                                onFocus={() => {
                                    setFormatDateVal(dateFormat2);
                                }}
                                name="toDate"
                                id="toDate"
                                // maxDate={now}
                                minDate={lazyParams.filters.fromDate.value}
                                dateFormat={formatDateVal}
                            ></Calendar>
                            {/* <InputText
                                style={{
                                    width: "100%",
                                }}
                                type="date"
                                id="toDate"
                                name="toDate"
                                value={lazyParams.filters.toDate.value}
                                max={now}
                                min={lazyParams.filters.fromDate.value}
                                onChange={(e) => {
                                    setCheckLoad(false);
                                    setLazyParams({
                                        ...lazyParams,
                                        filters: {
                                            ...lazyParams.filters,
                                            toDate: {
                                                ...lazyParams.filters.toDate,
                                                value: e.target.value,
                                            },
                                        },
                                    });
                                }}
                            ></InputText> */}
                        </div>
                    </div>
                </div>

                <div className="col-12 md:col-4 text-right">
                    <div className="grid custom-form">
                        <div className="col-12">
                            <label
                                style={{
                                    // width: "130px",
                                    // justifyContent: "end",
                                    display: "flex",
                                    padding: "0.75rem 0px 0px 0px",
                                }}
                            >
                                {" "}
                            </label>
                        </div>
                        <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                            <Button
                                label={t("search", { ns: "common" })}
                                icon="pi pi-search"
                                onClick={(e) => {
                                    if (lazyParams.filters.idNumber.value || lazyParams.filters.phoneNumber.value) {
                                        setCheckLoad(true);
                                        setLazyParams({
                                            ...lazyParams,
                                            first: 0,
                                            page: 0,
                                        });
                                    } else {
                                        if (lazyParams.filters.fromDate.value && lazyParams.filters.toDate.value) {
                                            if (checkDateMonth(lazyParams.filters.fromDate.value, lazyParams.filters.toDate.value, range2)) {
                                                setCheckLoad(true);
                                                setLazyParams({
                                                    ...lazyParams,
                                                    first: 0,
                                                    page: 0,
                                                });
                                            } else {
                                                toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("user.rangeFromToDate"), life: 3000 });
                                            }
                                        } else {
                                            toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("user.requiredfromandtodate"), life: 3000 });
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("user.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("user.list")}>
                                <Toast ref={toast} />
                                {/* <Can I={create} a={User}>
                        <Toolbar className="mb-4" left={leftToolbarTemplate} />
                    </Can> */}
                                <DataTable
                                    ref={dt}
                                    value={users}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    // globalFilter={globalFilter}
                                    // header={headerTemplate}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    // rowsPerPageOptions={[10, 20, 50]}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="username" sortable header={t("username")} showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="phoneNumber" sortable header={t("phoneNumber")} showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="fullName" sortable header={t("fullName")} showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="level" header={t("user.level")} showFilterMatchModes={false} body={LevelBodyTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}></Column>

                                    <Column field="type" header={t("type")} filter showFilterMatchModes={false} filterElement={userTypeFilterTemplate} body={userTypeBodyTemplate} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />

                                    <Column
                                        field="enabled"
                                        header={t("enabled")}
                                        dataType="boolean"
                                        filter
                                        showFilterMatchModes={false}
                                        filterElement={checkboxFilterTemplate}
                                        body={checkboxBodyTemplate}
                                        headerStyle={{ width: "10rem" }}
                                        style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                    />

                                    <Column header={t("status")} body={statusTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />

                                    <Column body={actionBodyTemplate} style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>
                    <UserEditor />
                </div>
            </div>
        </div>
    );
};
